import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReportPreview from "@/components/ReportPreview";
import VerdictBadge from "@/components/VerdictBadge";
import DealHistoryTimeline from "@/components/deal/DealHistoryTimeline";
import api, { API_BASE } from "@/lib/api";
import { strings } from "@/i18n/strings";
import { toast } from "sonner";
import { Download, ExternalLink, FileText, Loader2, Plus, RefreshCw } from "lucide-react";

const S = strings.reports;

export default function Reports({ dealId: forcedDealId, compact = false }) {
  const { id: routeId } = useParams();
  const dealId = forcedDealId || routeId; // Both /reports (no id) and /deals/:id reports tab supported
  const navigate = useNavigate();

  const [deals, setDeals] = useState([]);
  const [selectedId, setSelectedId] = useState(dealId || null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [rerunning, setRerunning] = useState({});

  // Load deals list (only if no fixed dealId)
  useEffect(() => {
    (async () => {
      try {
        if (!forcedDealId) {
          const { data } = await api.get("/deals");
          setDeals(data);
          if (!selectedId && data[0]) setSelectedId(data[0].id);
        }
      } finally {
        if (!forcedDealId) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedDealId]);

  // Load report for selected deal
  useEffect(() => {
    if (!selectedId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/deals/${selectedId}/report`);
        setReport(data);
      } catch {
        toast.error("Failed to load report.");
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedId]);

  const selectedDeal = useMemo(
    () => deals.find((d) => d.id === selectedId) || null,
    [deals, selectedId]
  );

  const missing = useMemo(() => {
    if (!report) return { ai: false, risk: false, neg: false };
    return {
      ai: !report.ai_recommendation,
      risk: !report.risk_analysis,
      neg: !report.offer_strategy,
    };
  }, [report]);

  const rerun = async (kind) => {
    const endpoint = kind === "ai" ? "analyze" : kind === "risk" ? "risks" : "negotiation";
    setRerunning((r) => ({ ...r, [kind]: true }));
    try {
      await api.post(`/deals/${selectedId}/${endpoint}`);
      const { data } = await api.get(`/deals/${selectedId}/report`);
      setReport(data);
      toast.success(`${kind === "ai" ? "AI" : kind === "risk" ? "Risk" : "Negotiation"} analysis refreshed.`);
    } catch {
      toast.error("Could not run AI — try again.");
    } finally {
      setRerunning((r) => ({ ...r, [kind]: false }));
    }
  };

  const downloadPdf = async () => {
    if (!selectedId) return;
    setDownloading(true);
    try {
      const response = await fetch(`${API_BASE}/deals/${selectedId}/report.pdf`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      // Try to use filename from Content-Disposition
      const cd = response.headers.get("content-disposition") || "";
      let filename = "runidea-report.pdf";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded.");
    } catch (e) {
      toast.error("Could not download PDF.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-32 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </AppShell>
    );
  }

  // Empty state — no deals at all
  if (!forcedDealId && deals.length === 0) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl py-16 text-center fade-in" data-testid="reports-empty">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <FileText className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-900">{S.empty_title}</h1>
          <p className="mt-2 text-sm text-slate-500">{S.empty_body}</p>
          <Button
            onClick={() => navigate("/deals/new")}
            className="mt-6 bg-indigo-600 text-white"
            data-testid="reports-empty-cta"
          >
            <Plus className="mr-2 h-4 w-4" /> {S.empty_cta}
          </Button>
        </div>
      </AppShell>
    );
  }

  const inner = (
    <div className={`fade-in ${compact ? "" : "mx-auto max-w-7xl"}`} data-testid="reports-root">
      {!compact && (
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">{S.title}</p>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{S.title}</h1>
            <p className="mt-3 max-w-2xl text-base text-slate-600">{S.subtitle}</p>
          </div>
        </div>
      )}

      <div className={`grid gap-6 ${compact ? "" : "lg:grid-cols-[1fr_320px]"}`}>
        {/* Preview */}
        <div className="min-w-0">
          {!forcedDealId && (
            <div className="mb-4 flex items-center gap-3">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{S.select_deal}</label>
              <Select value={selectedId || ""} onValueChange={setSelectedId}>
                <SelectTrigger className="w-[320px]" data-testid="reports-deal-select">
                  <SelectValue placeholder="Select a deal…" />
                </SelectTrigger>
                <SelectContent>
                  {deals.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.property?.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedDeal && <VerdictBadge verdict={selectedDeal.verdict} />}
            </div>
          )}
          {report ? (
            <ReportPreview report={report} />
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
              Select a deal to preview its report.
            </div>
          )}
          {/* Phase 36 Batch AE — Deal Event History timeline.
              Renders below the static report preview so users can scroll
              through every price change, AI run, stage move, and
              document event on this deal. Hidden until a deal is
              selected. */}
          {selectedId ? (
            <DealHistoryTimeline dealId={selectedId} />
          ) : null}
        </div>

        {/* Right rail / actions */}
        <aside className={compact ? "" : "lg:sticky lg:top-24 lg:self-start"}>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900">Actions</h3>
            <p className="mt-1 text-xs text-slate-500">{S.info}</p>
            <div className="mt-5 space-y-2.5">
              <Button
                onClick={downloadPdf}
                disabled={!selectedId || downloading}
                data-testid="reports-download-pdf"
                className="w-full bg-indigo-600 text-white transition-all active:scale-[0.98] hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
              >
                {downloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    {/* Phase 36 — label varies by role: investor vs listing pitch */}
                    {(report?.role || selectedDeal?.role || "buyer").toLowerCase() === "seller"
                      ? "Download Listing Pitch"
                      : "Download Investor Report"}
                  </>
                )}
              </Button>
              {selectedId && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/deals/${selectedId}`)}
                  data-testid="reports-open-deal"
                  className="w-full border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  <ExternalLink className="mr-2 h-4 w-4" /> {S.open_deal}
                </Button>
              )}
            </div>

            {(missing.ai || missing.risk || missing.neg) && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{S.rerun_ai}</h4>
                <div className="mt-2 space-y-1.5">
                  {missing.ai && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => rerun("ai")}
                      disabled={!!rerunning.ai}
                      data-testid="reports-rerun-ai"
                      className="w-full justify-start text-indigo-600 hover:bg-indigo-50"
                    >
                      {rerunning.ai ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                      Run AI analysis
                    </Button>
                  )}
                  {missing.risk && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => rerun("risk")}
                      disabled={!!rerunning.risk}
                      data-testid="reports-rerun-risk"
                      className="w-full justify-start text-indigo-600 hover:bg-indigo-50"
                    >
                      {rerunning.risk ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                      Run risk analysis
                    </Button>
                  )}
                  {missing.neg && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => rerun("neg")}
                      disabled={!!rerunning.neg}
                      data-testid="reports-rerun-neg"
                      className="w-full justify-start text-indigo-600 hover:bg-indigo-50"
                    >
                      {rerunning.neg ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                      Run negotiation
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );

  return compact ? inner : <AppShell>{inner}</AppShell>;
}
